import { Directive, ElementRef, booleanAttribute, inject, input } from '@angular/core';
import { focusControl, getActiveElement, getTabStops, indexOfTabStop, isPlainTab } from './focus-utils';

/**
 * Normalises `Tab` / `Shift+Tab` navigation inside a container so focus always moves to the next applicable editable
 * control in the visual and functional order of the page.
 *
 * Disabled, hidden and read-only controls are skipped. Standard browser behaviour is preserved at the boundaries of the
 * container: when the user tabs past the last (or before the first) control the event is left untouched so focus
 * escapes to the surrounding page as normal.
 *
 * Apply it to forms, dialogs, assessment sections and any other screen that needs a predictable tab sequence.
 *
 * @example
 *   ```html
 *   <form hxTabNav>
 *     ...
 *   </form>
 *
 *   <!-- Layouts whose visual order differs from the DOM order -->
 *   <div hxTabNav order="visual">...</div>
 *   ```;
 */
@Directive({
  selector: '[hxTabNav]',
  standalone: true,
  host: {
    '(keydown)': 'handleKeydown($event)',
    '(focusin)': 'handleFocusin($event)',
  },
})
export class HxTabNavigation {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private lastFocusedInside: HTMLElement | null = null;

  /**
   * How the editable controls are ordered.
   *
   * - `dom` (default) follows the document order, which matches the visual order for normal flow layouts.
   * - `visual` sorts by on-screen geometry (top-to-bottom, left-to-right) and should be used for flex/grid layouts that
   *   reorder their children.
   */
  readonly order = input<'dom' | 'visual'>('dom', { alias: 'hxTabNavOrder' });

  /** Disables the managed behaviour and falls back to the browser's native tab sequence. */
  readonly disabled = input(false, { alias: 'hxTabNavDisabled', transform: booleanAttribute });

  /** When true, tabbing past the last control wraps back to the first one instead of leaving the container. */
  readonly wrap = input(false, { alias: 'hxTabNavWrap', transform: booleanAttribute });

  protected handleFocusin(event: FocusEvent): void {
    const target = event.target as HTMLElement | null;
    if (target && this.host.nativeElement.contains(target)) {
      this.lastFocusedInside = target;
    }
  }

  protected handleKeydown(event: KeyboardEvent): void {
    if (this.disabled() || !isPlainTab(event) || event.defaultPrevented) {
      return;
    }

    let active = getActiveElement();
    const isInside = active && this.host.nativeElement.contains(active);

    // If focus was lost to document.body (e.g. the focused button became disabled or removed),
    // recover focus from the last known control inside this container instead of letting Tab escape.
    if (!isInside) {
      if ((active === document.body || !active) && this.lastFocusedInside) {
        active = this.lastFocusedInside;
      } else {
        return;
      }
    } else if (active) {
      this.lastFocusedInside = active;
    }

    const controls = getTabStops(this.host.nativeElement, this.order(), false, event.shiftKey);
    if (controls.length === 0) {
      return;
    }

    let currentIndex = indexOfTabStop(this.host.nativeElement, controls, active);
    if (currentIndex === -1) {
      // If the active control was removed or disabled (so it's no longer in controls),
      // find the closest subsequent stop based on DOM position.
      if (active) {
        currentIndex = controls.findIndex(
          (control) => Boolean(active!.compareDocumentPosition(control) & Node.DOCUMENT_POSITION_FOLLOWING)
        );
        if (currentIndex !== -1 && event.shiftKey) {
          currentIndex = Math.max(0, currentIndex - 1);
        }
      }
      if (currentIndex === -1) {
        return;
      }

      const delta = event.shiftKey ? -1 : 0;
      let nextIndex = currentIndex + delta;

      if (nextIndex < 0 || nextIndex >= controls.length) {
        if (!this.wrap()) {
          return;
        }
        nextIndex = (nextIndex + controls.length) % controls.length;
      }

      const next = controls[nextIndex];
      if (!next) {
        return;
      }

      event.preventDefault();
      this.lastFocusedInside = next;
      focusControl(next);
      return;
    }

    const delta = event.shiftKey ? -1 : 1;
    let nextIndex = currentIndex + delta;

    if (nextIndex < 0 || nextIndex >= controls.length) {
      if (!this.wrap()) {
        // Preserve standard behaviour: let the browser move focus out of the container.
        return;
      }
      nextIndex = (nextIndex + controls.length) % controls.length;
    }

    const next = controls[nextIndex];
    if (!next) {
      return;
    }

    event.preventDefault();
    this.lastFocusedInside = next;
    focusControl(next);
  }

  /** Programmatically focus the first applicable editable control in the container. */
  focusFirst(): boolean {
    const [first] = getTabStops(this.host.nativeElement, this.order());
    if (!first) {
      return false;
    }
    focusControl(first);
    return true;
  }
}
